import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';

/**
 * §11: "errors say what happened and never apologise vaguely."
 *
 * So `title` states the fact ("Your bank declined the card"), `body` says what
 * it usually means and what was NOT done ("Nothing was charged"), and there is
 * always a way forward. "Something went wrong, please try again later" is the
 * thing this component exists to prevent.
 */

export interface ErrorStateProps {
  /** What happened, stated plainly. Not "Oops!". */
  title: string;
  body?: string;
  illustration?: ReactNode;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  /** A quiet reassurance, e.g. "Your cart is saved — nothing is lost." */
  footnote?: string;
  className?: string;
}

export function ErrorState({
  title,
  body,
  illustration,
  action,
  secondaryAction,
  footnote,
  className,
}: ErrorStateProps) {
  return (
    <div role="alert" className={cn('px-[22px] py-[38px] text-center', className)}>
      <div className="mx-auto mb-4 h-[78px] w-[78px]">
        {illustration ?? <DeclinedCardArt />}
      </div>
      <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]">{title}</h2>
      {body ? (
        <p className="mx-auto mb-5 mt-[7px] max-w-[30ch] text-xs leading-relaxed text-muted">
          {body}
        </p>
      ) : (
        <div className="h-5" />
      )}
      <div className="space-y-[9px]">
        {action}
        {secondaryAction}
      </div>
      {footnote ? <p className="mt-4 text-[11px] text-faint">{footnote}</p> : null}
    </div>
  );
}

export function DeclinedCardArt() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden className="h-full w-full">
      <rect x="14" y="30" width="72" height="46" rx="8" fill="none" stroke="#C3D0E3" strokeWidth="4" />
      <path d="M14 46h72" stroke="#C3D0E3" strokeWidth="4" />
      <circle cx="72" cy="70" r="17" fill="#FBEAE9" />
      <path d="M72 61v10M72 76h.01" stroke="#C2261F" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

export function OfflineArt() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden className="h-full w-full">
      <path d="M12 38a56 56 0 0 1 76 0" fill="none" stroke="#C3D0E3" strokeWidth="5" strokeLinecap="round" />
      <path d="M26 54a36 36 0 0 1 48 0" fill="none" stroke="#C3D0E3" strokeWidth="5" strokeLinecap="round" />
      <path d="M38 68a18 18 0 0 1 24 0" fill="none" stroke="#0F52D9" strokeWidth="5" strokeLinecap="round" />
      <circle cx="50" cy="80" r="4.5" fill="#0F52D9" />
      <path d="M18 18 L82 82" stroke="#C2261F" strokeWidth="5" strokeLinecap="round" />
    </svg>
  );
}

/** §11 fifth state: offline, and still useful. */
export function OfflineBanner({ className }: { className?: string }) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-2 border-b border-caution/20 bg-caution-tint px-4 py-[9px]',
        className,
      )}
    >
      <Icon name="wifi-off" size={14} strokeWidth={2} className="text-caution" />
      <span className="text-[11px] font-bold text-caution-ink">You're offline</span>
    </div>
  );
}
